/**
 * WorkstationsDashboard — portada de Equipos (/infrastructure/workstations).
 *
 * Datos reales de `GET /api/workstations/overview`: una sola llamada con los
 * agregados materializados por el push del agente (BitLocker, parches,
 * antivirus, SO y riesgo). La lista completa sigue en /list.
 *
 * Reglas de la casa que se respetan:
 *  · nada de emojis: iconos de @heroicons;
 *  · colores solo con las variables del sistema de diseño (funciona con skins);
 *  · cada número lleva a la lista, no es un adorno;
 *  · todo lo que no se puede afirmar con datos se queda en «sin datos».
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  ChartBarIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SignalIcon,
  SignalSlashIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import VulnerabilitiesPanel from './VulnerabilitiesPanel';
import './WorkstationsDashboard.css';

const LIST_PATH = '/infrastructure/workstations/list';

const COLORS = {
  ok: 'var(--green-600, #16a34a)',
  warn: 'var(--amber-600, #d97706)',
  bad: 'var(--red-600, #dc2626)',
  info: 'var(--accent, #2563eb)',
  muted: 'var(--text-muted, #64748b)',
  gray: 'var(--slate-400, #94a3b8)',
};

const pct = (value, total) => (total ? Math.round((value / total) * 1000) / 10 : 0);

/** Cómo está instalado el agente. El parque mezcla MSI (GPO/Intune) y EXE. */
const CHANNEL_LABELS = [
  ['msi', 'MSI'],
  ['exe', 'EXE'],
  ['source', 'Código'],
  ['unknown', 'Sin identificar'],
];

function Donut({ segments, size = 136, thickness = 14, value, label }) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="wsd-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--bg-toolbar)"
          strokeWidth={thickness}
        />
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => {
            const length = (segment.value / total) * circumference;
            const dash = `${Math.max(0, length - 1.5)} ${circumference}`;
            const element = (
              <circle
                key={segment.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += length;
            return element;
          })}
      </svg>
      <div className="wsd-donut-center">
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

function Legend({ items, total }) {
  return (
    <ul className="wsd-legend">
      {items.map((item) => (
        <li key={item.key}>
          <span className="wsd-dot" style={{ background: item.color }} />
          <span className="wsd-legend-label">{item.label}</span>
          <span className="wsd-legend-value">{item.value}</span>
          <span className="wsd-legend-pct">{pct(item.value, total)}%</span>
        </li>
      ))}
    </ul>
  );
}

function StackedBar({ segments, total }) {
  return (
    <div className="wsd-stacked">
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <span
            key={segment.key}
            style={{ width: `${(segment.value / total) * 100}%`, background: segment.color }}
            title={`${segment.label}: ${segment.value}`}
          />
        ))}
    </div>
  );
}

function BarRow({ label, value, total, color, badge }) {
  return (
    <div className="wsd-bar-row">
      <span className="wsd-bar-label" title={label}>{label}</span>
      <span className="wsd-bar-track">
        <span style={{ width: `${pct(value, total)}%`, background: color }} />
      </span>
      <span className="wsd-bar-value">{value}</span>
      {badge && <span className={`wsd-mini-badge is-${badge.tone}`}>{badge.label}</span>}
    </div>
  );
}

function Panel({ icon: Icon, tone = 'info', title, subtitle, action, span = 4, children }) {
  return (
    <section className={`wsd-panel wsd-span-${span}`}>
      <header className="wsd-panel-head">
        <span className={`wsd-panel-icon wsd-tone-${tone}`}><Icon /></span>
        <div className="wsd-panel-titles">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action && <div className="wsd-panel-action">{action}</div>}
      </header>
      <div className="wsd-panel-body">{children}</div>
    </section>
  );
}

function KpiCard({ to, tone, icon: Icon, label, value, suffix, hint, bar }) {
  return (
    <Link to={to} className="wsd-kpi">
      <div className="wsd-kpi-top">
        <span className={`wsd-kpi-icon wsd-tone-${tone}`}><Icon /></span>
        <span className="wsd-kpi-label">{label}</span>
      </div>
      <div className="wsd-kpi-value">
        {value}
        {suffix && <small>{suffix}</small>}
      </div>
      {typeof bar === 'number' && (
        <div className="wsd-kpi-bar">
          <span className={`wsd-fill-${tone}`} style={{ width: `${bar}%` }} />
        </div>
      )}
      <div className="wsd-kpi-hint">{hint}</div>
    </Link>
  );
}

function SeverityPill({ severity }) {
  return (
    <span className={`wsd-pill is-${severity}`}>
      {severity === 'critical' ? 'Crítico' : 'Aviso'}
    </span>
  );
}

export default function WorkstationsDashboard({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const [searchParams] = useSearchParams();
  const socket = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const payload = await api.getWorkstationsOverview(companyId);
      setData(payload);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el resumen de equipos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Presencia en vivo: un equipo que entra o se cae cambia el KPI «Con agente»
  // sin esperar al refresco periódico. Se agrupa en 1,5 s para no pedir el
  // resumen una vez por agente cuando enciende una planta entera.
  useEffect(() => {
    if (!socket) return undefined;
    let timer = null;
    const schedule = (payload) => {
      if (
        payload?.company_id &&
        companyId &&
        Number(payload.company_id) !== Number(companyId)
      ) {
        return;
      }
      if (timer) return;
      timer = window.setTimeout(() => {
        timer = null;
        load({ silent: true });
      }, 1500);
    };
    const onConnect = () => load({ silent: true });
    socket.on('agent:presence', schedule);
    socket.on('connect', onConnect);
    return () => {
      if (timer) window.clearTimeout(timer);
      socket.off('agent:presence', schedule);
      socket.off('connect', onConnect);
    };
  }, [socket, companyId, load]);

  // Los enlaces antiguos que esperan la tabla pueden pedirlo explícitamente.
  if (searchParams.get('view') === 'list') {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (!companyId) {
    return (
      <div className="wsd">
        <div className="wsd-state">
          <ComputerDesktopIcon />
          <h2>{tr('wsd.selectCompany', 'Selecciona una empresa')}</h2>
          <p>{tr('wsd.selectCompanyHint', 'El resumen de equipos es por empresa.')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wsd">
        <header className="wsd-head">
          <div className="wsd-head-text">
            <div className="wsd-title-row">
              <h1>{tr('wsd.title', 'Equipos')}</h1>
            </div>
            <p>{tr('wsd.loading', 'Cargando el resumen de equipos…')}</p>
          </div>
        </header>
        <div className="wsd-skeleton" aria-hidden="true">
          {Array.from({ length: 9 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wsd">
        <header className="wsd-head">
          <div className="wsd-head-text">
            <div className="wsd-title-row">
              <h1>{tr('wsd.title', 'Equipos')}</h1>
            </div>
          </div>
        </header>
        <div className="wsd-state is-error">
          <ExclamationTriangleIcon />
          <h2>{tr('wsd.errorTitle', 'No se pudo cargar el resumen')}</h2>
          <p>{error}</p>
          <button type="button" className="wsd-btn wsd-btn--primary" onClick={() => load()}>
            {tr('wsd.retry', 'Reintentar')}
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.totals || !data.totals.total) {
    return (
      <div className="wsd">
        <header className="wsd-head">
          <div className="wsd-head-text">
            <div className="wsd-title-row">
              <h1>{tr('wsd.title', 'Equipos')}</h1>
            </div>
            <p>{tr('wsd.subtitle', 'Inventario, cifrado, actualizaciones y antivirus de los PCs y portátiles de la empresa.')}</p>
          </div>
        </header>
        <div className="wsd-state">
          <ComputerDesktopIcon />
          <h2>{tr('wsd.emptyTitle', 'Todavía no hay equipos')}</h2>
          <p>
            {tr(
              'wsd.emptyHint',
              'Da de alta PCs o portátiles y, cuando el agente reporte, aquí verás cifrado, parches y antivirus.'
            )}
          </p>
          <Link to={LIST_PATH} className="wsd-btn wsd-btn--primary">
            {tr('wsd.goList', 'Ir a la lista de equipos')}
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    );
  }

  const total = data.totals.total;

  // Última versión publicada en el servidor y equipos por debajo de ella.
  const latestAgentVersion = data.agents.latestVersion || null;
  const outdatedCount = data.agents.outdatedCount || 0;

  const agentSegments = [
    { key: 'online', label: tr('wsd.agent.online', 'Agente online'), value: data.agents.online, color: COLORS.ok },
    { key: 'offline', label: tr('wsd.agent.offline', 'Agente offline'), value: data.agents.offline, color: COLORS.warn },
    { key: 'none', label: tr('wsd.agent.none', 'Sin agente'), value: data.agents.none, color: COLORS.gray },
  ];

  const bitlockerSegments = [
    { key: 'protected', label: tr('wsd.bl.protected', 'Cifrado'), value: data.bitlocker.protected, color: COLORS.ok },
    { key: 'unprotected', label: tr('wsd.bl.unprotected', 'Sin cifrar'), value: data.bitlocker.unprotected, color: COLORS.bad },
    { key: 'progress', label: tr('wsd.bl.progress', 'En proceso'), value: data.bitlocker.inProgress, color: COLORS.warn },
    { key: 'na', label: tr('wsd.bl.na', 'No aplica'), value: data.bitlocker.notApplicable, color: COLORS.gray },
  ];

  const avSegments = [
    { key: 'protected', label: tr('wsd.av.protected', 'Activo y al día'), value: data.antivirus.protected, color: COLORS.ok },
    { key: 'outdated', label: tr('wsd.av.outdated', 'Firmas antiguas'), value: data.antivirus.outdated, color: COLORS.warn },
    { key: 'disabled', label: tr('wsd.av.disabled', 'Deshabilitado'), value: data.antivirus.disabled, color: COLORS.bad },
    { key: 'missing', label: tr('wsd.av.missing', 'Sin antivirus'), value: data.antivirus.missing, color: '#f97316' },
    { key: 'na', label: tr('wsd.av.na', 'No aplica'), value: data.antivirus.notApplicable, color: COLORS.gray },
    { key: 'unknown', label: tr('wsd.av.unknown', 'Sin datos'), value: data.antivirus.unknown, color: COLORS.muted },
  ];

  const updateSegments = [
    { key: 'ok', label: tr('wsd.up.ok', 'Al día'), value: data.updates.ok, color: COLORS.ok },
    { key: 'pending', label: tr('wsd.up.pending', 'Pendientes'), value: data.updates.pending, color: COLORS.warn },
    { key: 'critical', label: tr('wsd.up.critical', 'Críticas'), value: data.updates.critical, color: COLORS.bad },
    { key: 'unknown', label: tr('wsd.up.unknown', 'Sin datos'), value: data.updates.unknown, color: COLORS.gray },
  ];

  const agentCoverage = pct(data.agents.online, total);
  const bitlockerKnown =
    data.bitlocker.protected + data.bitlocker.unprotected + data.bitlocker.inProgress;
  const encrypted = bitlockerKnown ? pct(data.bitlocker.protected, bitlockerKnown) : null;
  const avKnown =
    data.antivirus.protected +
    data.antivirus.outdated +
    data.antivirus.disabled +
    data.antivirus.missing;
  const avActive = avKnown ? pct(data.antivirus.protected, avKnown) : null;
  const avProductsTotal = (data.antivirus.products || []).reduce((sum, product) => sum + product.total, 0);
  const generatedLabel = data.generatedAt
    ? new Date(data.generatedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : null;

  const listLink = (query = '') => `${LIST_PATH}${query}`;

  return (
    <div className="wsd">
      <header className="wsd-head">
        <div className="wsd-head-text">
          <div className="wsd-title-row">
            <h1>{tr('wsd.title', 'Equipos')}</h1>
            {generatedLabel && (
              <span className="wsd-updated">
                {tr('wsd.updatedAt', 'Actualizado a las')} {generatedLabel}
              </span>
            )}
          </div>
          <p>
            {tr(
              'wsd.subtitle',
              'Inventario, cifrado, actualizaciones y antivirus de los PCs y portátiles de la empresa.'
            )}
          </p>
        </div>
        <div className="wsd-head-actions">
          <button
            type="button"
            className="wsd-btn wsd-btn--ghost"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
          >
            <ArrowPathIcon className={refreshing ? 'is-spinning' : ''} />
            {refreshing ? tr('wsd.refreshing', 'Actualizando…') : tr('wsd.refresh', 'Actualizar')}
          </button>
          <Link to={listLink()} className="wsd-btn wsd-btn--primary">
            {tr('wsd.openList', 'Ver todos los equipos')}
            <ArrowRightIcon />
          </Link>
        </div>
      </header>

      <div className="wsd-kpis">
        <KpiCard
          to={listLink()}
          tone="info"
          icon={ComputerDesktopIcon}
          label={tr('wsd.kpi.total', 'Equipos')}
          value={data.totals.total}
          hint={`${data.totals.pcs} PC · ${data.totals.laptops} portátiles`}
        />
        <KpiCard
          to={listLink('?agent=online')}
          tone="ok"
          icon={SignalIcon}
          label={tr('wsd.kpi.agent', 'Con agente')}
          value={`${agentCoverage}%`}
          bar={agentCoverage}
          hint={
            outdatedCount > 0
              ? `${data.agents.online} de ${total} · ${outdatedCount} con versión desfasada`
              : `${data.agents.online} de ${total} · ${data.agents.none} sin agente`
          }
        />
        <KpiCard
          to={listLink('?bitlocker=protected')}
          tone="ok"
          icon={LockClosedIcon}
          label={tr('wsd.kpi.encrypted', 'Cifrado')}
          value={encrypted === null ? '—' : `${encrypted}%`}
          bar={encrypted ?? 0}
          hint={
            bitlockerKnown
              ? `${data.bitlocker.protected} de ${bitlockerKnown} con dato · ${data.bitlocker.unprotected} sin cifrar`
              : tr('wsd.kpi.noData', 'Sin datos de cifrado todavía')
          }
        />
        <KpiCard
          to={listLink('?updates=critical')}
          tone="bad"
          icon={ArrowPathIcon}
          label={tr('wsd.kpi.criticalUpdates', 'Parches')}
          value={data.updates.critical}
          hint={`${data.updates.critical} críticas · ${data.updates.pending} pendientes`}
        />
        <KpiCard
          to={listLink('?risk=critical')}
          tone="warn"
          icon={ExclamationTriangleIcon}
          label={tr('wsd.kpi.risk', 'En riesgo')}
          value={data.risk.critical}
          hint={`${data.risk.warning} avisos adicionales`}
        />
      </div>

      <div className="wsd-grid">
        <Panel
          icon={SignalIcon}
          tone={data.agents.none > 0 || outdatedCount > 0 ? 'warn' : 'ok'}
          title={tr('wsd.agent.title', 'Estado del agente')}
          subtitle={tr('wsd.agent.subtitle', 'Quién reporta, con qué versión y cómo se instaló')}
          action={<Link to={listLink('?agent=offline')} className="wsd-link">{tr('wsd.seeAll', 'Ver todos')}</Link>}
        >
          <div className="wsd-split">
            <Donut segments={agentSegments} value={`${agentCoverage}%`} label={tr('wsd.agent.center', 'con agente')} />
            <Legend items={agentSegments} total={total} />
          </div>
          <div className="wsd-note">
            <SignalSlashIcon />
            <span>
              <strong>{data.agents.offline7d}</strong> llevan más de 7 días sin reportar y{' '}
              <strong>{data.agents.offline30d}</strong> más de 30.
            </span>
          </div>
          <div className="wsd-subblock">
            <span className="wsd-subblock-title">
              {tr('wsd.agent.versions', 'Versiones del agente')}
              {latestAgentVersion ? ` · ${tr('wsd.agent.latestShort', 'última')} v${latestAgentVersion}` : ''}
            </span>
            {data.agents.versions.map((item) => (
              <BarRow
                key={item.version}
                label={`v${item.version}`}
                value={item.count}
                total={data.agents.online + data.agents.offline}
                color={latestAgentVersion && item.version === latestAgentVersion ? COLORS.ok : COLORS.warn}
              />
            ))}
          </div>

          {data.agents.channels && (
            <div className="wsd-subblock">
              <span className="wsd-subblock-title">{tr('wsd.agent.channels', 'Instalación')}</span>
              <div className="wsd-chips">
                {CHANNEL_LABELS.filter(([key]) => (data.agents.channels[key] || 0) > 0).map(([key, label]) => (
                  <span className={`wsd-chip${key === 'unknown' ? ' is-muted' : ''}`} key={key}>
                    {label} <strong>{data.agents.channels[key]}</strong>
                  </span>
                ))}
                {!CHANNEL_LABELS.some(([key]) => (data.agents.channels[key] || 0) > 0) && (
                  <span className="wsd-subblock-empty">
                    {tr('wsd.agent.noChannels', 'Sin datos de instalación todavía')}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="wsd-subblock">
            <span className="wsd-subblock-title">
              {tr('wsd.agent.outdated', 'Versiones desfasadas')}
              {outdatedCount > 0 ? ` · ${outdatedCount}` : ''}
            </span>
            {!latestAgentVersion ? (
              <span className="wsd-subblock-empty">
                {tr('wsd.agent.noRelease', 'Sin instalador publicado: no hay versión con la que comparar.')}
              </span>
            ) : data.agents.online + data.agents.offline === 0 ? (
              <span className="wsd-subblock-empty">
                {tr('wsd.agent.noAgentsYet', 'Todavía no hay ningún equipo con agente instalado.')}
              </span>
            ) : (data.agents.outdated || []).length === 0 ? (
              <span className="wsd-subblock-empty">
                {tr('wsd.agent.upToDate', 'Todos los equipos con agente están en la última versión.')}
              </span>
            ) : (
              (data.agents.outdated || []).map((device) => (
                <Link to={listLink('?outdated=1')} className="wsd-mini-row wsd-mini-row--link" key={device.code}>
                  <span className="wsd-mini-code">{device.code}</span>
                  <span className="wsd-mini-detail">
                    v{device.current} → v{device.latest}
                    {device.channel && device.channel !== 'unknown'
                      ? ` · ${device.channel.toUpperCase()}`
                      : ''}
                  </span>
                  <span className="wsd-mini-time">{device.lastSeen}</span>
                </Link>
              ))
            )}
          </div>
          {(data.agents.devices || []).length > 0 && (
            <div className="wsd-subblock">
              <span className="wsd-subblock-title">{tr('wsd.agent.noneList', 'Sin agente')}</span>
              {(data.agents.devices || []).map((device) => (
                <div className="wsd-mini-row" key={device.code}>
                  <span className="wsd-mini-code">{device.code}</span>
                  <span className="wsd-mini-detail">{device.detail}</span>
                  <span className="wsd-mini-time">{device.lastSeen}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          icon={LockClosedIcon}
          tone={data.bitlocker.unprotected > 0 ? 'bad' : 'ok'}
          title={tr('wsd.bl.title', 'Cifrado de disco')}
          subtitle={tr('wsd.bl.subtitle', 'BitLocker · último dato reportado por el agente')}
          action={<Link to={listLink('?bitlocker=unprotected')} className="wsd-link">{tr('wsd.seeAll', 'Ver todos')}</Link>}
        >
          <div className="wsd-split">
            <Donut segments={bitlockerSegments} value={encrypted === null ? '—' : `${encrypted}%`} label={tr('wsd.bl.center', 'cifrado')} />
            <Legend items={bitlockerSegments} total={bitlockerKnown} />
          </div>
          {(data.bitlocker.devices || []).length > 0 && (
            <div className="wsd-subblock">
              <span className="wsd-subblock-title">{tr('wsd.bl.unprotectedList', 'Equipos sin cifrar')}</span>
              {(data.bitlocker.devices || []).slice(0, 3).map((device) => (
                <div className="wsd-mini-row" key={device.code}>
                  <span className="wsd-mini-code">{device.code}</span>
                  <span className="wsd-mini-detail">{device.detail}</span>
                  <span className="wsd-mini-time">{device.lastSeen}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          icon={ShieldCheckIcon}
          tone={data.antivirus.disabled + data.antivirus.missing > 0 ? 'bad' : 'ok'}
          title={tr('wsd.av.title', 'Antivirus y EDR')}
          subtitle={tr('wsd.av.subtitle', 'Qué producto protege cada equipo y cuáles no están al día')}
          action={<Link to={listLink('?av=disabled')} className="wsd-link">{tr('wsd.seeAll', 'Ver todos')}</Link>}
        >
          <div className="wsd-split">
            <Donut segments={avSegments} value={avActive === null ? '—' : `${avActive}%`} label={tr('wsd.av.center', 'activo y al día')} />
            <Legend items={avSegments} total={avKnown} />
          </div>
          {(data.antivirus.products || []).length > 0 && (
            <div className="wsd-subblock">
              <span className="wsd-subblock-title">{tr('wsd.av.products', 'Productos detectados')}</span>
              {(data.antivirus.products || []).map((product) => {
                const incidents = product.outdated + product.disabled;
                return (
                  <BarRow
                    key={product.name}
                    label={product.name}
                    value={product.total}
                    total={avProductsTotal}
                    color={incidents > 0 ? COLORS.warn : COLORS.ok}
                    badge={incidents > 0 ? { label: `${incidents} incidencias`, tone: 'warn' } : null}
                  />
                );
              })}
            </div>
          )}
        </Panel>

        <Panel
          icon={ArrowPathIcon}
          tone={data.updates.critical > 0 ? 'warn' : 'ok'}
          title={tr('wsd.up.title', 'Windows Update')}
          subtitle={tr('wsd.up.subtitle', 'Pendientes, críticas y reinicios')}
          action={<Link to={listLink('?updates=critical')} className="wsd-link">{tr('wsd.seeAll', 'Ver todos')}</Link>}
          span={6}
        >
          <StackedBar segments={updateSegments} total={total} />
          <Legend items={updateSegments} total={total} />
          <div className="wsd-note">
            <WrenchScrewdriverIcon />
            <span>
              <strong>{data.updates.rebootRequired}</strong> equipos con reinicio pendiente y{' '}
              <strong>{data.updates.critical}</strong> con actualizaciones críticas.
            </span>
          </div>
          {(data.updates.devices || []).length > 0 && (
            <div className="wsd-subblock">
              <span className="wsd-subblock-title">{tr('wsd.up.worst', 'Más atrasados')}</span>
              {(data.updates.devices || []).slice(0, 4).map((device) => (
                <div className="wsd-mini-row" key={device.code}>
                  <span className="wsd-mini-code">{device.code}</span>
                  <span className="wsd-mini-detail">{device.detail}</span>
                  <span className="wsd-mini-time">{device.lastSeen}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          icon={ChartBarIcon}
          tone="info"
          title={tr('wsd.os.title', 'Sistema operativo')}
          subtitle={tr('wsd.os.subtitle', 'Reparto de la flota y versiones sin soporte')}
          span={6}
        >
          {data.os.map((item) => (
            <BarRow
              key={item.key}
              label={item.name}
              value={item.count}
              total={total}
              color={item.tone === 'critical' ? COLORS.bad : COLORS.info}
              badge={item.tone === 'critical' ? { label: 'Sin soporte', tone: 'bad' } : null}
            />
          ))}
          <div className="wsd-note">
            <ExclamationTriangleIcon />
            <span>
              <strong>{data.os.find((item) => item.key === 'win10')?.count}</strong> equipos siguen en
              Windows 10, sin actualizaciones de seguridad.
            </span>
          </div>
        </Panel>

        <Panel
          icon={ExclamationTriangleIcon}
          tone="bad"
          title={tr('wsd.risk.title', 'Requieren atención')}
          subtitle={tr('wsd.risk.subtitle', 'Reglas: sin agente, sin cifrar, críticas, antivirus y sistema sin soporte')}
          action={<Link to={listLink()} className="wsd-link">{tr('wsd.seeAll', 'Ver todos')}</Link>}
          span={8}
        >
          <div className="wsd-table-wrap">
            <table className="wsd-table">
              <thead>
                <tr>
                  <th>{tr('wsd.col.severity', 'Severidad')}</th>
                  <th>{tr('wsd.col.device', 'Equipo')}</th>
                  <th>{tr('wsd.col.user', 'Usuario')}</th>
                  <th>{tr('wsd.col.os', 'Sistema')}</th>
                  <th>{tr('wsd.col.reason', 'Motivo')}</th>
                  <th>{tr('wsd.col.seen', 'Última vez')}</th>
                  <th aria-label="abrir" />
                </tr>
              </thead>
              <tbody>
                {(data.attention || []).map((row) => (
                  <tr key={row.code}>
                    <td><SeverityPill severity={row.severity} /></td>
                    <td><span className="wsd-table-code">{row.code}</span></td>
                    <td className="wsd-table-muted">{row.user}</td>
                    <td className="wsd-table-muted">{row.os}</td>
                    <td>{row.reason}</td>
                    <td className="wsd-table-muted">{row.lastSeen}</td>
                    <td><Link to={listLink()} className="wsd-row-link" aria-label={`Abrir ${row.code}`}><ArrowRightIcon /></Link></td>
                  </tr>
                ))}
                {(data.attention || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="wsd-table-empty">
                      {tr('wsd.risk.empty', 'Nada urgente: ningún equipo dispara las reglas de riesgo.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel
          icon={BuildingOfficeIcon}
          tone="info"
          title={tr('wsd.inv.title', 'Inventario')}
          subtitle={tr('wsd.inv.subtitle', 'Fabricantes y huecos de información')}
        >
          <div className="wsd-subblock">
            <span className="wsd-subblock-title">{tr('wsd.inv.manufacturers', 'Fabricantes')}</span>
            {data.manufacturers.map((item) => (
              <BarRow key={item.name} label={item.name} value={item.count} total={total} color={COLORS.info} />
            ))}
          </div>
          <div className="wsd-mini-cards">
            <Link to={listLink()} className="wsd-mini-card">
              <UserIcon />
              <strong>{data.unassigned}</strong>
              <span>{tr('wsd.inv.unassigned', 'sin usuario asignado')}</span>
            </Link>
            <Link to={listLink()} className="wsd-mini-card">
              <SignalSlashIcon />
              <strong>{data.withoutInventory}</strong>
              <span>{tr('wsd.inv.noInventory', 'sin inventario reciente')}</span>
            </Link>
          </div>
        </Panel>
      </div>

      <VulnerabilitiesPanel companyId={companyId} ciType="workstation" />

      <footer className="wsd-foot">
        <span className="wsd-foot-dot" />
        {tr(
          'wsd.foot',
          'Datos del inventario del agente. Los equipos sin agente aparecen como «sin datos», no como «sin cifrar».'
        )}
      </footer>
    </div>
  );
}
